import { useState, useEffect, useCallback, useRef } from "react";
import "./Teamoverview.css";

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "https://api.growthstationco.com";

const POLL_INTERVAL = 30_000;
const ACTIVE_STATUSES = ["active", "remote", "meeting"];
// ✅ FIX: dayoff is open but should NOT be treated as active/running
const OPEN_STATUSES = ["active", "remote", "break", "meeting", "dayoff"];
const BATCH_SIZE = 3;
const BATCH_DELAY = 300;

function getAuthHeaders() {
  const token = localStorage.getItem("token") || "";
  return { "Content-Type": "application/json", Authorization: token };
}

async function parseResponseBody(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function formatLocalYYYYMMDD(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function isTodayStr(dateStr) {
  return dateStr === formatLocalYYYYMMDD(new Date());
}

function normalizeLogStatus(raw) {
  const s = String(raw ?? "").toLowerCase().trim();
  if (!s) return "offline";
  if (["day off", "day-off", "day_off", "dayoff"].includes(s)) return "dayoff";
  if (s.includes("break") || s === "on-break") return "break";
  if (["off", "offline"].includes(s)) return "offline";
  if (["remote", "wfh", "work from home"].includes(s)) return "remote";
  if (["active", "online", "working"].includes(s)) return "active";
  if (["meeting", "in meeting"].includes(s)) return "meeting";
  return "offline";
}

function chunkArray(arr, size) {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size)
    chunks.push(arr.slice(i, i + size));
  return chunks;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ✅ FIX: كاش بسيط بيحتفظ بآخر logs ناجحة لكل (userId + date). لو
// السيرفر رجّع 429 لأي يوزر، بدل ما نصفّر وقته فورًا على "offline"
// (اللي كان بيخلي الرقم يفضل ثابت/صفر طول ما الـ 429 مستمر)، بنرجّع
// آخر نسخة معروفة من بياناته بدل ما نمسحها، ونعمل retry بـ backoff
// قبل ما نستسلم نهائيًا.
const lastKnownGoodLogs = new Map(); // key: `${uid}:${date}` → cleanedLogs array

function cacheKey(uid, date) {
  return `${uid}:${date}`;
}

const RETRY_429_DELAYS = [800, 1600, 3200]; // ms — backoff تصاعدي

async function fetchWithRetryOn429(url, headers, maxRetries = RETRY_429_DELAYS.length) {
  let res;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    res = await fetch(url, {
      headers,
      cache: "no-store",   // ✅ FIX: يمنع الـ 304/browser cache خالص
    });
    if (res.status !== 429) return res;
    if (attempt < maxRetries) {
      await sleep(RETRY_429_DELAYS[attempt]);
    }
  }
  return res;
}

// ─── API ──────────────────────────────────────────────────────────────────────

async function getAllUsersActivityByDay(date = null) {
  const selectedDate = date || formatLocalYYYYMMDD(new Date());

  const usersRes = await fetch(`${BASE_URL}/employee/`, {
    headers: getAuthHeaders(),
    cache: "no-store", 
  });
  const usersJson = await parseResponseBody(usersRes);
  if (!usersRes.ok) throw usersJson;

  const users = Array.isArray(usersJson)
    ? usersJson
    : usersJson?.data ?? usersJson?.users ?? [];

  if (!users.length) return { data: [] };

  const makeOffline = (userBase) => [
    {
      status: "offline",
      startTime: null,
      endTime: null,
      _day: selectedDate,
      userId: userBase,
    },
  ];

  async function fetchUserLogs(user) {
    const uid = user._id ?? user.id;
    if (!uid) return null;

    const userBase = {
      _id: uid,
      userName: user.userName ?? user.name ?? "Unknown",
      avatar: user.avatar ?? null,
      department: user.department ?? "—",
      position: user.position ?? "—",
      role: user.role ?? "—",
    };

    const key = cacheKey(uid, selectedDate);

    try {
      // ✅ FIX: استخدم fetchWithRetryOn429 بدل fetch العادي — لو
      // السيرفر بيرجع 429 بيعمل إعادة محاولة بـ backoff تصاعدي
      // (0.8s ثم 1.6s ثم 3.2s) قبل ما يستسلم.
      const res = await fetchWithRetryOn429(
        `${BASE_URL}/log/user/${uid}?date=${selectedDate}&_t=${Date.now()}`,
        getAuthHeaders()
      );

      if (res.status === 429) {
        // ✅ FIX: حتى بعد كل محاولات الـ retry، لو لسه 429، رجّع
        // آخر نسخة معروفة من بيانات اليوزر ده (لو موجودة) بدل ما
        // تصفّر وقته بالكامل على "offline". كده الرقم في الواجهة
        // بيفضل صحيح (آخر قيمة حقيقية) لحد ما السيرفر يرد بنجاح،
        // بدل ما يتجمد على 0h 0m / offline كل polling cycle.
        const cached = lastKnownGoodLogs.get(key);
        if (cached) {
          console.warn(
            `Rate-limited (429) for user ${uid} on ${selectedDate} — using last known good data`
          );
          return cached;
        }
        return makeOffline(userBase);
      }

      const data = await parseResponseBody(res);
      if (!res.ok || !data) {
        // فشل غير 429 (500 مثلاً) — استخدم الكاش لو موجود بدل ما نصفّر
        const cached = lastKnownGoodLogs.get(key);
        if (cached) return cached;
        return makeOffline(userBase);
      }

      const rawLogs = Array.isArray(data) ? data : data?.data ?? [];
      if (!rawLogs.length) return makeOffline(userBase);

      const cleanedLogs = rawLogs
        .filter((log) => {
          if (!log?.startTime) return false;
          const start = new Date(log.startTime);
          if (isNaN(start.getTime())) return false;
          return formatLocalYYYYMMDD(start) === selectedDate;
        })
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
        .map((log) => ({
          ...log,
          status: normalizeLogStatus(log.status),
          _day: selectedDate,
          userId: userBase,
        }));

      const result = cleanedLogs.length ? cleanedLogs : makeOffline(userBase);

      // ✅ خزّن النسخة الناجحة دي كـ "آخر بيانات معروفة" لليوزر ده/اليوم ده
      if (cleanedLogs.length) {
        lastKnownGoodLogs.set(key, result);
      }

      return result;
    } catch (err) {
      console.error("User logs error:", uid, err);
      const cached = lastKnownGoodLogs.get(key);
      if (cached) return cached;
      return makeOffline(userBase);
    }
  }

  const batches = chunkArray(users, BATCH_SIZE);
  const allResults = [];

  for (let i = 0; i < batches.length; i++) {
    const batchResults = await Promise.allSettled(
      batches[i].map((user) => fetchUserLogs(user))
    );
    allResults.push(...batchResults);
    if (i < batches.length - 1) await sleep(BATCH_DELAY);
  }

  const allLogs = allResults
    .filter((r) => r.status === "fulfilled" && r.value)
    .flatMap((r) => r.value);

  return { data: allLogs };
}

// ─── Time helpers ─────────────────────────────────────────────────────────────

function msToHM(ms) {
  if (!ms || ms < 0) return "0h 0m";
  const totalMin = Math.floor(ms / 60_000);
  return `${Math.floor(totalMin / 60)}h ${totalMin % 60}m`;
}

/**
 * ✅ FIXED: Compute time per status correctly.
 *
 * Key fixes:
 * 1. Each log's duration is determined by its own [startTime → endTime].
 *    If no endTime, use next log's startTime as the boundary (not now).
 *    Only the LAST open log (no endTime, no next log) uses `now` as end — and only if today.
 * 2. dayoff is treated as a full-day status: if the user has a dayoff log,
 *    we mark the full working window as dayoff but still respect actual logged times.
 * 3. No double-counting: each millisecond belongs to exactly one status.
 * 4. ✅ NEW FIX: an explicit endTime can never push the computed duration past
 *    "now" for today's date. Previously, if a log carried a future/scheduled
 *    endTime (e.g. a shift's planned end-of-day time), the code counted the
 *    full duration up to that future point immediately — inflating today's
 *    totals before that time had actually passed. We now clamp the end of
 *    every interval to `now` whenever selectedDate is today.
 * 5. ✅ NEWEST FIX: offline logs are KEPT in the sorted sequence (instead of
 *    being filtered out before pairing). Previously, removing offline logs
 *    before computing "next log's startTime" as a boundary meant an
 *    offline gap between two active/remote/etc. periods (e.g. active →
 *    offline for lunch → active again) was invisible to the loop: the
 *    first "active" log would look past the offline log entirely and use
 *    the SECOND active log's startTime as its end — silently swallowing
 *    the whole offline gap into active time and inflating totals. Now
 *    offline logs still act as boundary markers (closing out whatever
 *    status came before them) but contribute 0 time themselves, since the
 *    switch statement below has no case for "offline".
 */
function computeTimeFromLogs(userLogs, selectedDate, now) {
  if (!userLogs?.length)
    return {
      activeMs: 0,
      breakMs: 0,
      meetingMs: 0,
      remoteMs: 0,
      dayoffMs: 0,
    };

  const dayStart = new Date(selectedDate);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(selectedDate);
  dayEnd.setHours(23, 59, 59, 999);
  const isToday = isTodayStr(selectedDate);

  // ✅ FIX: keep offline logs here so they still close out the previous
  // status's interval correctly — only filter on having a valid startTime.
  const sorted = [...userLogs]
    .filter((l) => l.startTime)
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

  let activeMs = 0,
    breakMs = 0,
    meetingMs = 0,
    remoteMs = 0,
    dayoffMs = 0;

  for (let i = 0; i < sorted.length; i++) {
    const log = sorted[i];
    const startRaw = new Date(log.startTime).getTime();
    if (isNaN(startRaw)) continue;

    // Clamp start to the selected day
    const start = Math.max(startRaw, dayStart.getTime());

    let endRaw = null;

    if (log.endTime) {
      // Explicit end time — but may be a *scheduled/future* end, not an
      // actual one. We still read it here; it gets clamped to `now` below.
      endRaw = new Date(log.endTime).getTime();
    } else if (sorted[i + 1]?.startTime) {
      // Next log starts (could now be an "offline" log) → this log ends
      // at that point. This is what fixes the swallowed-gap bug.
      endRaw = new Date(sorted[i + 1].startTime).getTime();
    } else {
      // Last log with no endTime
      if (!isToday) continue; // Past day → skip open-ended logs (no real data)
      if (log.status === "dayoff") {
        // Day off → count until end of day or now, whichever is earlier
        endRaw = Math.min(now, dayEnd.getTime());
      } else if (OPEN_STATUSES.includes(log.status)) {
        // Still running today
        endRaw = now;
      } else {
        continue;
      }
    }

    if (!endRaw || isNaN(endRaw)) continue;

    // Clamp end to the selected day boundaries, AND to `now` if it's today —
    // this is the core fix for inflated hours: a future/scheduled endTime
    // (e.g. 6pm shift-end logged in advance) must never count time that
    // hasn't actually elapsed yet.
    const end = Math.min(
      endRaw,
      dayEnd.getTime(),
      isToday ? now : Infinity
    );
    const duration = Math.max(0, end - start);
    if (duration <= 0) continue;

    // ✅ Accumulate into the correct bucket — no mixing.
    // "offline" intentionally has no case here: it only acts as a
    // boundary marker (see above), it never accrues time itself.
    switch (log.status) {
      case "active":
        activeMs += duration;
        break;
      case "break":
        breakMs += duration;
        break;
      case "meeting":
        meetingMs += duration;
        break;
      case "remote":
        remoteMs += duration;
        break;
      case "dayoff":
        dayoffMs += duration;
        break;
      // offline logs intentionally fall through with no bucket.
    }
  }

  return { activeMs, breakMs, meetingMs, remoteMs, dayoffMs };
}

/**
 * ✅ FIXED: resolveCurrentStatus
 *
 * Problems fixed:
 * - dayoff was falling through to "offline" because the old code only kept
 *   the status when `!end && OPEN_STATUSES.includes(latest.status)`.
 *   But dayoff logs often DO have an endTime (end of day), so they were
 *   being returned as-is but then the outer code treated them as offline.
 * - ✅ NEW FIX: previously, if the latest log had an `endTime` that had
 *   already passed (e.g. a scheduled/expected shift-end time stamped in
 *   advance), the user was force-flipped to "offline" even though they
 *   were still actively logged in/working past that time (overtime, or
 *   the endTime field representing a planned time rather than an actual
 *   logout). We no longer use `endTime` as the deciding factor for
 *   "is this user offline right now" — only an explicit "offline" status,
 *   or the absence of any open status, makes a user offline. A newer log
 *   superseding this one is already handled naturally since that newer
 *   log becomes `latest`.
 */
function resolveCurrentStatus(userLogs, now) {
  if (!userLogs?.length) return "offline";

  const sorted = [...userLogs]
    .filter((l) => l.startTime)
    .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

  if (!sorted.length) return "offline";

  const latest = sorted[0];
  const normalizedStatus = latest.status || "offline";

  // ✅ FIX: مبقاش فيه استثناء خاص بـ "dayoff" يدور على أي log تاني في
  // اليوم. آخر log فعلي (بالـ startTime) هو اللي بيحدد الحالة الحالية
  // دايمًا — سواء كان dayoff أو offline أو active أو أي حاجة تانية.
  // الطريقة القديمة كانت بتخلي أي log بـ dayoff في أي وقت من اليوم
  // "يقفز" فوق آخر status حقيقي لو ده كان offline، وده كان بيرجّع
  // status قديم غلط بدل الأحدث.
  if (normalizedStatus === "offline") return "offline";

  if (!OPEN_STATUSES.includes(normalizedStatus)) return "offline";

  if (latest.endTime) {
    const endTs = new Date(latest.endTime).getTime();
    if (!isNaN(endTs) && endTs <= now) {
      return "offline";
    }
  }

  return normalizedStatus;
}

function computeUserStats(logs, selectedDate) {
  const now = Date.now();
  const byUser = {};

  for (const log of logs) {
    const userId = log.userId;
    const uid =
      userId?._id ??
      userId?.id ??
      (typeof userId === "string" ? userId : null);
    if (!uid) continue;

    if (!byUser[uid]) {
      byUser[uid] = {
        info: {
          _id: uid,
          userName:
            userId?.userName ?? userId?.name ?? log.userName ?? "Unknown",
          avatar: userId?.avatar ?? null,
          department: userId?.department ?? "—",
          position: userId?.position ?? "—",
          role: userId?.role ?? "—",
        },
        logs: [],
      };
    }
    byUser[uid].logs.push(log);
  }

  return Object.values(byUser).map(({ info, logs: userLogs }) => {
    const day =
      userLogs[0]?._day || selectedDate || formatLocalYYYYMMDD(new Date());
    const currentStatus = resolveCurrentStatus(userLogs, now);
    const { activeMs, breakMs, meetingMs, remoteMs, dayoffMs } =
      computeTimeFromLogs(userLogs, day, now);
    return { info, currentStatus, activeMs, breakMs, meetingMs, remoteMs, dayoffMs };
  });
}

// ─── UI config ────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  active:  { label: "Active",   color: "#22c55e", bg: "rgba(34,197,94,0.12)"   },
  break:   { label: "On Break", color: "#f59e0b", bg: "rgba(245,158,11,0.12)"  },
  remote:  { label: "Remote",   color: "#06b6d4", bg: "rgba(6,182,212,0.12)"   },
  dayoff:  { label: "Day Off",  color: "#8b5cf6", bg: "rgba(139,92,246,0.12)"  },
  meeting: { label: "Meeting",  color: "#ec4899", bg: "rgba(236,72,153,0.12)"  },
  offline: { label: "Offline",  color: "#6b7280", bg: "rgba(107,114,128,0.12)" },
};

function getStatus(s) {
  return STATUS_CONFIG[s] ?? STATUS_CONFIG.offline;
}

const AVATAR_COLORS = [
  "#6366f1","#0ea5e9","#10b981","#f59e0b","#ec4899","#8b5cf6","#14b8a6",
];
function avatarColor(name) {
  return AVATAR_COLORS[(name?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];
}

function Avatar({ name, size = 36 }) {
  const initials = name
    ? name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()
    : "?";
  return (
    <div
      className="table-avatar"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.38,
        background: avatarColor(name),
      }}
    >
      {initials}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function TeamOverview() {
  const [users,            setUsers]            = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [silentRefreshing, setSilentRefreshing] = useState(false);
  const [error,            setError]            = useState(null);
  const [search,           setSearch]           = useState("");
  const [deptFilter,       setDeptFilter]       = useState("all");
  const [statusFilter,     setStatusFilter]     = useState("all");
  const [selectedDate,     setSelectedDate]     = useState(formatLocalYYYYMMDD(new Date()));
  const [lastRefresh,      setLastRefresh]      = useState(null);
  const fetchIdRef = useRef(0);

  const load = useCallback(
    async ({ silent = false, retryCount = 0 } = {}) => {
      const fetchId = ++fetchIdRef.current;
      try {
        setError(null);
        if (silent) setSilentRefreshing(true);
        else setLoading(true);

        const result = await getAllUsersActivityByDay(selectedDate);
        if (fetchId !== fetchIdRef.current) return;

        setUsers(computeUserStats(result.data || [], selectedDate));
        setLastRefresh(new Date());
      } catch (e) {
        if (fetchId !== fetchIdRef.current) return;
        console.error("Load error:", e);
        if (retryCount < 2) {
          setTimeout(() => load({ silent, retryCount: retryCount + 1 }), 2_000);
          return;
        }
        setError("⚠ Connection issue — data may be outdated.");
      } finally {
        if (fetchId === fetchIdRef.current) {
          setLoading(false);
          setSilentRefreshing(false);
        }
      }
    },
    [selectedDate]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let timeout;
    const handle = () => {
      clearTimeout(timeout);
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = 800;
        oscillator.type = "sine";
        gainNode.gain.value = 0.3;
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.2);
      } catch (err) {
      }
      timeout = setTimeout(() => load({ silent: true }), 1_500);
    };
    window.addEventListener("userStatusChanged", handle);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener("userStatusChanged", handle);
    };
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => load({ silent: true }), POLL_INTERVAL);
    return () => clearInterval(id);
  }, [load]);

  // ─── Derived ────────────────────────────────────────────────────────────────
  const departments = [
    "all",
    ...new Set(
      users.map((u) => u.info.department).filter((d) => d && d !== "—")
    ),
  ];

  const filtered = users.filter((u) => {
    const n = u.info.userName.toLowerCase().includes(search.toLowerCase());
    const d = deptFilter === "all" || u.info.department === deptFilter;
    const s = statusFilter === "all" || u.currentStatus === statusFilter;
    return n && d && s;
  });

  const counts = {
    active:  users.filter((u) => u.currentStatus === "active").length,
    break:   users.filter((u) => u.currentStatus === "break").length,
    remote:  users.filter((u) => u.currentStatus === "remote").length,
    dayoff:  users.filter((u) => u.currentStatus === "dayoff").length,
    meeting: users.filter((u) => u.currentStatus === "meeting").length,
    offline: users.filter((u) => u.currentStatus === "offline").length,
  };

  const maxActive = Math.max(...users.map((u) => u.activeMs + u.remoteMs + u.breakMs + u.meetingMs), 1);

  const donutData = [
    { label: "Active",  count: counts.active,  color: getStatus("active").color  },
    { label: "Break",   count: counts.break,   color: getStatus("break").color   },
    { label: "Remote",  count: counts.remote,  color: getStatus("remote").color  },
    { label: "Meeting", count: counts.meeting, color: getStatus("meeting").color },
    { label: "Day Off", count: counts.dayoff,  color: getStatus("dayoff").color  },
    { label: "Offline", count: counts.offline, color: getStatus("offline").color },
  ];

  const total = donutData.reduce((s, d) => s + d.count, 0) || 1;
  let angle = -90;
  const r = 60, cx = 80, cy = 80;
  const arcs = donutData
    .filter((d) => d.count > 0)
    .map((d) => {
      const deg = (d.count / total) * 360;
      const s = (angle * Math.PI) / 180;
      const e = ((angle + deg) * Math.PI) / 180;
      const x1 = cx + r * Math.cos(s), y1 = cy + r * Math.sin(s);
      const x2 = cx + r * Math.cos(e), y2 = cy + r * Math.sin(e);
      angle += deg;
      return {
        ...d,
        path: `M ${x1} ${y1} A ${r} ${r} 0 ${deg > 180 ? 1 : 0} 1 ${x2} ${y2}`,
      };
    });

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="overview-page">
      <div className="overview-header">
        <div>
          <h1>Team Overview</h1>
          {lastRefresh && (
            <p className="overview-header-meta">
              Last updated {lastRefresh.toLocaleTimeString()}
              {silentRefreshing && (
                <span className="silent-refresh-indicator"> · Updating…</span>
              )}
            </p>
          )}
        </div>
        <div className="overview-header-actions">
          <input
            type="date"
            className="overview-date-input"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
          <button
            className="overview-refresh-btn"
            onClick={() => load()}
            disabled={loading}
          >
            <svg
              width={14}
              height={14}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className={loading ? "spin" : ""}
            >
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="overview-error">{error}</div>}

      <div className="status-summary">
        {[
          { key: "active",  label: "Active"   },
          { key: "break",   label: "On Break" },
          { key: "remote",  label: "Remote"   },
          { key: "meeting", label: "Meeting"  },
          { key: "dayoff",  label: "Day Off"  },
        ].map(({ key, label }) => {
          const cfg = getStatus(key);
          return (
            <div
              key={key}
              className="status-summary-card"
              style={{ borderColor: cfg.color + "44", background: cfg.bg }}
            >
              <span
                className="status-summary-dot"
                style={{ background: cfg.color }}
              />
              <div className="status-summary-body">
                <div
                  className="status-summary-count"
                  style={{ color: cfg.color }}
                >
                  {counts[key]}
                </div>
                <div className="status-summary-label">{label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {users.length > 0 && (
        <div className="charts-row">
          <div className="chart-box">
            <h2>Active Time</h2>
            <div className="bar-chart">
              {users.slice(0, 10).map((u) => {
                const cfg = getStatus(u.currentStatus);
                const pct = (u.activeMs / maxActive) * 100;
                return (
                  <div className="bar-col" key={u.info._id}>
                    <div
                      className="bar-fill"
                      style={{
                        height: `${Math.max(pct, 4)}%`,
                        background: cfg.color,
                        opacity: 0.8,
                      }}
                      title={`${u.info.userName}: ${msToHM(u.activeMs)}`}
                    />
                    <span className="bar-label">
                      {u.info.userName.split(" ")[0]}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="chart-box">
            <h2>Status Distribution</h2>
            <div className="donut-wrap">
              <svg width={160} height={160} viewBox="0 0 160 160">
                {arcs.length === 0 && (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={r}
                    fill="none"
                    stroke="#334155"
                    strokeWidth={28}
                  />
                )}
                {arcs.map((arc, i) => (
                  <path
                    key={i}
                    d={arc.path}
                    fill="none"
                    stroke={arc.color}
                    strokeWidth={28}
                    strokeLinecap="butt"
                  />
                ))}
                <text
                  x={cx}
                  y={cy - 6}
                  textAnchor="middle"
                  fontSize={20}
                  fontWeight={700}
                  fill="white"
                >
                  {total}
                </text>
                <text
                  x={cx}
                  y={cy + 10}
                  textAnchor="middle"
                  fontSize={11}
                  fill="#94a3b8"
                >
                  Total
                </text>
              </svg>
              <div className="donut-legend">
                {donutData.map((d, i) => (
                  <div className="donut-legend-item" key={i}>
                    <span
                      className="donut-legend-dot"
                      style={{ background: d.color }}
                    />
                    <span>{d.label}</span>
                    <span className="donut-legend-count">{d.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="overview-controls">
        <input
          type="text"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
        >
          {departments.map((d) => (
            <option key={d} value={d}>
              {d === "all" ? "All Departments" : d}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {["all", "active", "break", "remote", "meeting", "dayoff", "offline"].map(
            (s) => (
              <option key={s} value={s}>
                {s === "all" ? "All Statuses" : getStatus(s).label}
              </option>
            )
          )}
        </select>
      </div>

      <div className="overview-table-wrap">
        <table className="overview-table">
          <thead>
            <tr>
              {[
                "Name","Dept","Position","Role","Status",
                "Active","Break","Meeting","Remote","Day Off",
              ].map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={10} className="table-empty">
                  Loading team data…
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={10} className="table-empty">
                  No users found.
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((u) => {
                const cfg = getStatus(u.currentStatus);
                return (
                  <tr key={u.info._id}>
                    <td>
                      <div className="table-name-cell">
                        <Avatar name={u.info.userName} size={28} />
                        <span className="table-name">{u.info.userName}</span>
                      </div>
                    </td>
                    <td style={{ color: "#94a3b8" }}>{u.info.department}</td>
                    <td style={{ color: "#94a3b8" }}>{u.info.position}</td>
                    <td style={{ color: "#94a3b8" }}>{u.info.role}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{
                          color: cfg.color,
                          background: cfg.bg,
                          borderColor: cfg.color + "44",
                          padding: "4px 10px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: 500,
                          border: "1px solid",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <span
                          style={{
                            background: cfg.color,
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            display: "inline-block",
                          }}
                        />
                        {cfg.label}
                      </span>
                    </td>
                    <td
                      style={{
                        fontWeight: 600,
                        color: ACTIVE_STATUSES.includes(u.currentStatus)
                          ? cfg.color
                          : "#94a3b8",
                      }}
                    >
                      {msToHM(u.activeMs)}
                    </td>
                    <td
                      style={{
                        fontWeight: u.currentStatus === "break" ? 600 : 400,
                        color:
                          u.currentStatus === "break"
                            ? getStatus("break").color
                            : "#94a3b8",
                      }}
                    >
                      {msToHM(u.breakMs)}
                    </td>
                    <td
                      style={{
                        fontWeight: u.currentStatus === "meeting" ? 600 : 400,
                        color:
                          u.currentStatus === "meeting"
                            ? getStatus("meeting").color
                            : "#94a3b8",
                      }}
                    >
                      {msToHM(u.meetingMs)}
                    </td>
                    <td
                      style={{
                        fontWeight: u.currentStatus === "remote" ? 600 : 400,
                        color:
                          u.currentStatus === "remote"
                            ? getStatus("remote").color
                            : "#94a3b8",
                      }}
                    >
                      {msToHM(u.remoteMs)}
                    </td>
                    <td
                      style={{
                        fontWeight: u.currentStatus === "dayoff" ? 600 : 400,
                        color:
                          u.currentStatus === "dayoff"
                            ? getStatus("dayoff").color
                            : "#94a3b8",
                      }}
                    >
                      {msToHM(u.dayoffMs)}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}